/**
 * Runtime Tenant Branding Hook
 * Fetches branding from API instead of environment variables
 */

import { useState, useEffect } from 'react';
import { TenantBranding, fetchTenantConfig, generateColorShades } from '../config/tenant-runtime';

interface UseTenantBrandingReturn {
  branding: TenantBranding;
  colors: {
    primary: string;
    lighter: string;
    darker: string;
  };
  isLoading: boolean;
  error: string | null;
}

/**
 * Hook to get and apply tenant branding from API
 */
export const useTenantBranding = (): UseTenantBrandingReturn => {
  const [isLoading, setIsLoading] = useState(true);
  const [branding, setBranding] = useState<TenantBranding | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadTenantBranding = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // Fetch tenant config from API
        const tenantConfig = await fetchTenantConfig();
        const tenantBranding = tenantConfig.branding;
        
        // Update document title
        document.title = tenantBranding.title;
        
        // Update favicon if provided
        if (tenantBranding.favicon) {
          updateFavicon(tenantBranding.favicon);
        }
        
        // Apply CSS custom properties for branding
        const colors = generateColorShades(tenantBranding.primaryColor);
        applyCSSProperties(colors);
        
        setBranding(tenantBranding);
        setIsLoading(false);
        
      } catch (error) {
        console.error('Failed to load tenant branding:', error);
        setError(error instanceof Error ? error.message : 'Unknown error');
        
        // Set fallback branding
        const fallbackBranding: TenantBranding = {
          tenantId: 'bloom',
          title: 'Bloom Health Platform',
          logo: '/5-axis-logo.svg',
          favicon: '/favicon.svg',
          companyName: '5 Axis Health',
          primaryColor: '#3B82F6'
        };
        
        setBranding(fallbackBranding);
        setIsLoading(false);
      }
    };

    loadTenantBranding();
  }, []);

  const colors = branding ? generateColorShades(branding.primaryColor) : {
    primary: '#3B82F6',
    lighter: '#60A5FA',
    darker: '#1E40AF'
  };

  return {
    branding: branding || {
      tenantId: 'bloom',
      title: 'Bloom Health Platform',
      logo: '/5-axis-logo.svg',
      favicon: '/favicon.svg',
      companyName: '5 Axis Health',
      primaryColor: '#3B82F6'
    },
    colors,
    isLoading,
    error
  };
};

/**
 * Update the page favicon dynamically
 */
const updateFavicon = (faviconUrl: string) => {
  // Update standard favicon
  const favicon = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
  if (favicon) {
    favicon.href = faviconUrl;
  }

  // Update Apple touch icon
  const appleIcon = document.querySelector('link[rel="apple-touch-icon"]') as HTMLLinkElement;
  if (appleIcon) {
    appleIcon.href = faviconUrl;
  }
};

/**
 * Apply CSS custom properties for tenant branding
 */
const applyCSSProperties = (colors: { primary: string; lighter: string; darker: string }) => {
  const root = document.documentElement;
  
  // Set CSS custom properties for brand colors
  root.style.setProperty('--brand-primary', colors.primary);
  root.style.setProperty('--brand-primary-light', colors.lighter);
  root.style.setProperty('--brand-primary-dark', colors.darker);
  
  // Set Tailwind CSS custom properties
  root.style.setProperty('--tw-brand-primary', colors.primary);
  root.style.setProperty('--tw-brand-primary-light', colors.lighter);
  root.style.setProperty('--tw-brand-primary-dark', colors.darker);
  
  console.log(`🎨 Applied tenant brand colors: ${colors.primary}`);
};

/**
 * Hook to check if a feature is enabled
 */
export const useFeatureFlag = (feature: string): boolean => {
  const [isEnabled, setIsEnabled] = useState(false);
  
  useEffect(() => {
    const checkFeature = async () => {
      try {
        const tenantConfig = await fetchTenantConfig();
        setIsEnabled(tenantConfig.features.enabled.includes(feature));
      } catch (error) {
        console.warn(`Cannot check feature flag ${feature}:`, error);
        setIsEnabled(false);
      }
    };
    
    checkFeature();
  }, [feature]);
  
  return isEnabled;
};
